/**
 * Default configuration for the Pythagoras Trees application
 */
import { FRACTAL_CONSTANTS, COLOR_CONSTANTS, CANVAS_CONSTANTS } from './constants.js';

export const defaultConfig = {
    // Visual parameters
    triangleAngle: FRACTAL_CONSTANTS.DEFAULT_TRIANGLE_ANGLE, // Base angle in degrees
    color1: COLOR_CONSTANTS.DEFAULT_PRIMARY_COLOR, // Primary color
    color2: COLOR_CONSTANTS.DEFAULT_SECONDARY_COLOR, // Secondary color
    backgroundColor: COLOR_CONSTANTS.DEFAULT_BACKGROUND_COLOR, // Canvas background color
    colorPattern: 'classic', // Active color pattern

    // Generation parameters
    recursionDepth: FRACTAL_CONSTANTS.DEFAULT_RECURSION_DEPTH, // Maximum tree depth
    baseSquareSize: FRACTAL_CONSTANTS.DEFAULT_BASE_SQUARE_SIZE, // Starting square size in pixels

    // Animation parameters
    animationSpeed: FRACTAL_CONSTANTS.DEFAULT_ANIMATION_SPEED, // Animation speed multiplier
    animateGeneration: true, // Enable progressive generation animation
    levelDelay: CANVAS_CONSTANTS.LEVEL_DELAY_MS, // Delay between levels in milliseconds
    polygonDelay: CANVAS_CONSTANTS.POLYGON_DELAY_MS, // Delay between polygons within level
    showLevelIndicator: true, // Show current level number during animation

    // Interaction parameters
    minZoom: 0.1, // Minimum zoom level
    maxZoom: 10.0, // Maximum zoom level
    zoomSensitivity: 0.1, // Mouse wheel zoom sensitivity
    pinchSensitivity: 0.01, // Touch pinch zoom sensitivity
    panBoundary: 2000, // Maximum pan distance from origin
    smoothZoom: true, // Enable smooth zoom transitions
    panInertia: true, // Enable pan momentum

    // Color picker settings
    colorPicker: {
        useNative: true, // Use native HTML5 color input
        showHexValues: true, // Display hex values alongside pickers
        validateInput: true, // Validate color format
        fallbackToText: true, // Use text input fallback if unsupported
    },

    // Export parameters
    exportFormat: 'png', // Default export format
    exportPreset: 'hd', // Default quality preset
    exportPNG: {
        width: 2048, // Default PNG export width
        height: 2048, // Default PNG export height
        quality: 1.0, // PNG quality (0.0-1.0)
        antiAliasing: true, // Enable anti-aliasing for PNG
    },
    exportSVG: {
        width: 1024, // Default SVG viewport width
        height: 1024, // Default SVG viewport height
        precision: 2, // Coordinate decimal precision
        optimize: true, // Optimize SVG output
    },
    exportAutoFit: {
        enabled: true, // Enable auto-fitting
        padding: 50, // Padding around tree
        centerTree: true, // Center tree in export
    },

    // Display options
    showLevelNumbers: false, // Show level numbers on squares
    antiAliasing: true, // Enable canvas anti-aliasing
    drawBorder: false, // Draw black outline borders around polygons
    endWithTriangles: false, // End tree generation with triangles instead of squares
};

/**
 * Parameter constraints
 */
export const CONSTRAINTS = {
    MIN_ANGLE: 5, // Minimum triangle base angle
    MAX_ANGLE: 85, // Maximum triangle base angle
    MIN_DEPTH: 1, // Minimum recursion depth
    MAX_DEPTH: 15, // Maximum recursion depth
    MIN_BASE_SIZE: 20, // Minimum base square size
    MAX_BASE_SIZE: 300, // Maximum base square size
    MIN_ANIMATION_SPEED: 0.1, // Minimum animation speed
    MAX_ANIMATION_SPEED: 5.0, // Maximum animation speed
};

/**
 * Performance limits
 */
export const PERFORMANCE_LIMITS = {
    MAX_VISIBLE_SQUARES: 250_000, // Stop rendering beyond this (allows for depth 16 with headroom)
    MIN_SQUARE_SIZE_PIXELS: 1, // Don't render squares smaller than this
    MAX_RECURSION_MOBILE: 14, // Lower limit on mobile devices
    ANIMATION_TIMEOUT_MS: 5000, // Stop animation if it takes too long
};

/**
 * Export quality presets
 */
export const exportPresets = {
    web: { width: 1024, height: 1024, quality: 0.9 },
    hd: { width: 1920, height: 1920, quality: 1.0 },
    uhd: { width: 3840, height: 3840, quality: 1.0 },
    print: { width: 4096, height: 4096, quality: 1.0 },
};

/**
 * Color pattern definitions for different rendering styles
 */
export const colorPatterns = {
    // Group 1: Basic Patterns (Simple, Foundational)
    classic: {
        name: 'Classic',
        description: 'Traditional square-only pattern (current default)',
        showSquares: true,
        showTriangles: false,
        renderSquares: (square, config) => (square.level % 2 === 0 ? config.color1 : config.color2),
        renderTriangles: null,
    },

    monochrome: {
        name: 'Monochrome classic',
        description: 'Single color for squares only',
        showSquares: true,
        showTriangles: false,
        renderSquares: (square, config) => config.color1,
        renderTriangles: null,
    },

    fullMonochrome: {
        name: 'Full monochrome',
        description: 'Single color for the whole tree (squares and triangles)',
        showSquares: true,
        showTriangles: true,
        renderSquares: (square, config) => config.color1,
        renderTriangles: (triangle, config) => config.color1,
    },

    inverse: {
        name: 'Inverse classic',
        description: 'Inverted classic pattern colors',
        showSquares: true,
        showTriangles: false,
        renderSquares: (square, config) => (square.level % 2 === 0 ? config.color2 : config.color1),
        renderTriangles: null,
    },

    // Group 2: Level-Based Patterns (Structure-Driven)
    levelMatching: {
        name: 'Level Matching',
        description: 'Squares and triangles match by level',
        showSquares: true,
        showTriangles: true,
        renderSquares: (square, config) => (square.level % 2 === 0 ? config.color1 : config.color2),
        renderTriangles: (triangle, config) =>
            triangle.level % 2 === 0 ? config.color1 : config.color2,
    },

    alternating: {
        name: 'Alternating',
        description: 'Cross-pattern coloring for visual contrast',
        showSquares: true,
        showTriangles: true,
        renderSquares: (square, config) => config.color1,
        renderTriangles: (triangle, config) => config.color2,
    },

    // Group 3: Gradient Patterns (Smooth Transitions)
    gradientSquares: {
        name: 'Gradient Squares',
        description: 'Progressive color blending on squares only',
        showSquares: true,
        showTriangles: false,
        renderSquares: (square, config, maxLevel, allSquares) => {
            // Calculate actual max level from the current tree data
            const actualMaxLevel =
                allSquares && allSquares.length > 0
                    ? Math.max(...allSquares.map(s => s.level))
                    : maxLevel;
            return interpolateColor(config.color1, config.color2, square.level / actualMaxLevel);
        },
        renderTriangles: null,
    },

    fullGradient: {
        name: 'Full Gradient',
        description: 'Gradient applied to both squares and triangles',
        showSquares: true,
        showTriangles: true,
        renderSquares: (square, config, maxLevel, allSquares) => {
            // Calculate actual max level from the current tree data
            const actualMaxLevel =
                allSquares && allSquares.length > 0
                    ? Math.max(...allSquares.map(s => s.level))
                    : maxLevel;
            return interpolateColor(config.color1, config.color2, square.level / actualMaxLevel);
        },
        renderTriangles: (triangle, config, maxLevel, allSquares, allTriangles) => {
            // Calculate actual max level from the current triangle data
            const actualMaxLevel =
                allTriangles && allTriangles.length > 0
                    ? Math.max(...allTriangles.map(t => t.level))
                    : maxLevel;
            return interpolateColor(config.color1, config.color2, triangle.level / actualMaxLevel);
        },
    },

    // Group 4: Advanced/Special Effects
    rainbow: {
        name: 'Rainbow',
        description: 'Colorful spectrum cycling influenced by user colors',
        showSquares: true,
        showTriangles: true,
        renderSquares: (square, config) => {
            // Get user colors in HSV to influence the rainbow
            const color1Hsv = hexToHsv(config.color1);
            const color2Hsv = hexToHsv(config.color2);

            // Create a rainbow that cycles through spectrum but is influenced by user colors
            // Use user colors' saturation and value as base, but cycle through all hues
            const baseHue = (square.level * 45) % 360; // Cycle through spectrum faster than original

            // Blend the saturation and value from user colors
            const avgSaturation = (color1Hsv.s + color2Hsv.s) / 2;
            const avgValue = (color1Hsv.v + color2Hsv.v) / 2;

            // Add some variation based on user color hues
            const userHueInfluence = ((color1Hsv.h + color2Hsv.h) / 2 + square.level * 15) % 360;
            const finalHue = (baseHue + userHueInfluence * 0.3) % 360;

            // Use high saturation and value for vibrant rainbow, but influenced by user colors
            const saturation = Math.max(70, avgSaturation * 0.7 + 30);
            const value = Math.max(80, avgValue * 0.8 + 20);

            return hsvToHex(finalHue, saturation, value);
        },
        renderTriangles: (triangle, config) => {
            // Similar to squares but with offset and different cycling
            const color1Hsv = hexToHsv(config.color1);
            const color2Hsv = hexToHsv(config.color2);

            const baseHue = (triangle.level * 45 + 180) % 360; // Offset from squares

            const avgSaturation = (color1Hsv.s + color2Hsv.s) / 2;
            const avgValue = (color1Hsv.v + color2Hsv.v) / 2;

            // Different user hue influence for triangles
            const userHueInfluence = ((color1Hsv.h + color2Hsv.h) / 2 + triangle.level * 20) % 360;
            const finalHue = (baseHue + userHueInfluence * 0.4) % 360;

            const saturation = Math.max(65, avgSaturation * 0.6 + 35);
            const value = Math.max(75, avgValue * 0.7 + 25);

            return hsvToHex(finalHue, saturation, value);
        },
    },

    outline: {
        name: 'Outline shapes',
        description: 'Outline the shapes with color 1',
        showSquares: true,
        showTriangles: true,
        renderSquares: (square, config) => ({ stroke: config.color1, fill: null, lineWidth: 2 }),
        renderTriangles: (triangle, config) => ({
            stroke: config.color1,
            fill: null,
            lineWidth: 2,
        }),
    },

    outlinePicture: {
        name: 'Outline picture',
        description: 'Outline the whole picture with color 1',
        showSquares: true,
        showTriangles: false,
        renderSquares: (square, config, maxLevel) => {
            // Draw only external edges based on square position
            if (square.level === 0) {
                // First level (base): draw 3 sides (bottom, left, right - not top where triangle connects)
                return {
                    stroke: config.color1,
                    fill: null,
                    lineWidth: 2,
                    edges: ['bottom', 'left', 'right'],
                };
            } else if (square.level === maxLevel) {
                // Last level: draw 3 sides (top, left, right - not bottom where they connect to triangles)
                return {
                    stroke: config.color1,
                    fill: null,
                    lineWidth: 2,
                    edges: ['top', 'left', 'right'],
                };
            } else {
                // Intermediate levels: draw 2 sides (left and right - external edges)
                return {
                    stroke: config.color1,
                    fill: null,
                    lineWidth: 2,
                    edges: ['left', 'right'],
                };
            }
        },
        renderTriangles: null, // Never draw triangles for outline picture
    },
};

/**
 * Get color pattern by name
 * @param {string} patternName - Pattern name
 * @returns {Object|null} Pattern configuration or null if not found
 */
export function getColorPattern(patternName) {
    return colorPatterns[patternName] || null;
}

/**
 * Get list of available color pattern names
 * @returns {Array} Array of pattern names
 */
export function getColorPatternNames() {
    return Object.keys(colorPatterns);
}

/**
 * Color utility functions for patterns
 */

/**
 * Linear interpolation between two colors
 * @param {string} color1 - Start color (hex)
 * @param {string} color2 - End color (hex)
 * @param {number} factor - Interpolation factor (0-1)
 * @returns {string} Interpolated color (hex)
 */
export function interpolateColor(color1, color2, factor) {
    const c1 = hexToRgb(color1);
    const c2 = hexToRgb(color2);

    if (!c1 || !c2) {
        return color1;
    } // Fallback if color parsing fails

    const r = Math.round(c1.r + (c2.r - c1.r) * factor);
    const g = Math.round(c1.g + (c2.g - c1.g) * factor);
    const b = Math.round(c1.b + (c2.b - c1.b) * factor);

    return rgbToHex(r, g, b);
}

/**
 * Convert HSV to HEX
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} v - Value (0-100)
 * @returns {string} HEX color
 */
export function hsvToHex(h, s, v) {
    const c = (v / 100) * (s / 100);
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = v / 100 - c;

    let r, g, b;
    if (h < 60) {
        r = c;
        g = x;
        b = 0;
    } else if (h < 120) {
        r = x;
        g = c;
        b = 0;
    } else if (h < 180) {
        r = 0;
        g = c;
        b = x;
    } else if (h < 240) {
        r = 0;
        g = x;
        b = c;
    } else if (h < 300) {
        r = x;
        g = 0;
        b = c;
    } else {
        r = c;
        g = 0;
        b = x;
    }

    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);

    return rgbToHex(r, g, b);
}

/**
 * Convert HEX to RGB
 * @param {string} hex - HEX color
 * @returns {Object|null} RGB object {r, g, b} or null if invalid
 */
export function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16),
          }
        : null;
}

/**
 * Convert RGB to HEX
 * @param {number} r - Red (0-255)
 * @param {number} g - Green (0-255)
 * @param {number} b - Blue (0-255)
 * @returns {string} HEX color
 */
export function rgbToHex(r, g, b) {
    return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

/**
 * Convert HEX to HSV
 * @param {string} hex - HEX color
 * @returns {Object} HSV object {h, s, v}
 */
export function hexToHsv(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb) {
        return { h: 0, s: 0, v: 0 };
    }

    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;

    let h = 0;
    let s = max === 0 ? 0 : delta / max;
    let v = max;

    if (delta !== 0) {
        if (max === r) {
            h = ((g - b) / delta) % 6;
        } else if (max === g) {
            h = (b - r) / delta + 2;
        } else {
            h = (r - g) / delta + 4;
        }
    }

    h = Math.round(h * 60);
    if (h < 0) {
        h += 360;
    }
    s = Math.round(s * 100);
    v = Math.round(v * 100);

    return { h, s, v };
}

/**
 * Interpolate between two hue values, taking the shortest path around the color wheel
 * @param {number} hue1 - Start hue (0-360)
 * @param {number} hue2 - End hue (0-360)
 * @param {number} factor - Interpolation factor (0-1)
 * @param {number} offset - Optional hue offset in degrees
 * @returns {number} Interpolated hue (0-360)
 */
export function interpolateHue(hue1, hue2, factor, offset = 0) {
    // Calculate the shortest path between hues
    let diff = hue2 - hue1;

    // Normalize to [-180, 180] range for shortest path
    if (diff > 180) {
        diff -= 360;
    } else if (diff < -180) {
        diff += 360;
    }

    // Interpolate
    let result = hue1 + diff * factor + offset;

    // Normalize to [0, 360) range
    while (result < 0) {
        result += 360;
    }
    while (result >= 360) {
        result -= 360;
    }

    return Math.round(result);
}

/**
 * Check if a square is on the outer boundary of the fractal
 * @param {Square} square - Square to check
 * @param {Array} allSquares - All squares in the fractal
 * @param {Array} allTriangles - All triangles in the fractal
 * @returns {boolean} True if square is on outer boundary
 */
export function isOuterBoundarySquare(square, allSquares) {
    // A square is on the outer boundary if:
    // 1. It's at the maximum level (leaf nodes)
    // 2. It's the base square (level 0)

    const maxLevel = allSquares.length > 0 ? Math.max(...allSquares.map(s => s.level)) : 0;

    // All squares at the maximum level are boundary squares (leaf nodes)
    if (square.level === maxLevel) {
        return true;
    }

    // Base square (level 0) is always on the boundary
    if (square.level === 0) {
        return true;
    }

    // For intermediate levels, check if it's on the left or right edge
    const squaresAtSameLevel = allSquares.filter(s => s.level === square.level);
    if (squaresAtSameLevel.length <= 2) {
        // If there are only 1 or 2 squares at this level, they're all on the boundary
        return true;
    }

    // Sort squares at this level by X coordinate to find leftmost and rightmost
    squaresAtSameLevel.sort((a, b) => a.x - b.x);
    const leftmost = squaresAtSameLevel[0];
    const rightmost = squaresAtSameLevel[squaresAtSameLevel.length - 1];

    // Use coordinate comparison instead of object comparison
    return (
        (Math.abs(square.x - leftmost.x) < 0.001 && Math.abs(square.y - leftmost.y) < 0.001) ||
        (Math.abs(square.x - rightmost.x) < 0.001 && Math.abs(square.y - rightmost.y) < 0.001)
    );
}

/**
 * Check if a triangle is on the outer boundary of the fractal
 * @param {Triangle} triangle - Triangle to check
 * @param {Array} allSquares - All squares in the fractal
 * @param {Array} allTriangles - All triangles in the fractal
 * @returns {boolean} True if triangle is on outer boundary
 */
export function isOuterBoundaryTriangle(triangle, allSquares, allTriangles) {
    // A triangle is on the outer boundary if:
    // 1. It's at the maximum level (leaf nodes)

    const maxLevel = allTriangles.length > 0 ? Math.max(...allTriangles.map(t => t.level)) : 0;

    // All triangles at the maximum level are boundary triangles
    if (triangle.level === maxLevel) {
        return true;
    }

    // For intermediate levels, check if it's on the left or right edge
    const trianglesAtSameLevel = allTriangles.filter(t => t.level === triangle.level);
    if (trianglesAtSameLevel.length <= 2) {
        // If there are only 1 or 2 triangles at this level, they're all on the boundary
        return true;
    }

    // Sort triangles at this level by X coordinate of their centroid
    trianglesAtSameLevel.sort((a, b) => {
        const aCentroidX = (a.vertices[0].x + a.vertices[1].x + a.vertices[2].x) / 3;
        const bCentroidX = (b.vertices[0].x + b.vertices[1].x + b.vertices[2].x) / 3;
        return aCentroidX - bCentroidX;
    });

    const leftmost = trianglesAtSameLevel[0];
    const rightmost = trianglesAtSameLevel[trianglesAtSameLevel.length - 1];

    // Use coordinate comparison instead of object comparison
    const triangleCentroidX =
        (triangle.vertices[0].x + triangle.vertices[1].x + triangle.vertices[2].x) / 3;
    const triangleCentroidY =
        (triangle.vertices[0].y + triangle.vertices[1].y + triangle.vertices[2].y) / 3;

    const leftmostCentroidX =
        (leftmost.vertices[0].x + leftmost.vertices[1].x + leftmost.vertices[2].x) / 3;
    const leftmostCentroidY =
        (leftmost.vertices[0].y + leftmost.vertices[1].y + leftmost.vertices[2].y) / 3;

    const rightmostCentroidX =
        (rightmost.vertices[0].x + rightmost.vertices[1].x + rightmost.vertices[2].x) / 3;
    const rightmostCentroidY =
        (rightmost.vertices[0].y + rightmost.vertices[1].y + rightmost.vertices[2].y) / 3;

    return (
        (Math.abs(triangleCentroidX - leftmostCentroidX) < 0.001 &&
            Math.abs(triangleCentroidY - leftmostCentroidY) < 0.001) ||
        (Math.abs(triangleCentroidX - rightmostCentroidX) < 0.001 &&
            Math.abs(triangleCentroidY - rightmostCentroidY) < 0.001)
    );
}
